import { calendarBag, cycleLabel } from './cycles.js';
import { err, ok, type CalendarResult } from './errors.js';
import { canonicalDay as brandDay } from './instant.js';
import {
  dayOfYear,
  monthAndDayFromDayOfYear,
  monthLength,
  monthsCycle,
  weekdaysCycle,
  yearLength,
} from './months.js';
import {
  type Calendar,
  type CanonicalDay,
  type DisplayDate,
  type Era,
  type ExpandedDisplayDate,
} from './types.js';

/**
 * Canonical-day-count ↔ display-date conversion.
 *
 * Design notes:
 *
 * - The canonical axis is a signed integer day count on a shared,
 *   calendar-independent line (ADR-0006 §2). A calendar projects onto it
 *   via `anchor_canonical_day`: the canonical day at which the calendar's
 *   ORIGIN display date (first era, `year_one`, month 1, day 1) sits.
 * - Internally, conversion runs in two steps:
 *
 *     1. Translate the display date `(era, year, month, day)` to an
 *        intermediate offset — the number of days from the era's first
 *        day to the target. This step encapsulates era direction
 *        (forward / backward) and year-numbering quirks.
 *     2. Translate the offset to the canonical axis by adding the era's
 *        `start_day`, subtracting the (pre-anchor) canonical position of
 *        the origin display date, and adding `anchor_canonical_day`. The
 *        engine is self-consistent: round-trip of the origin date always
 *        returns `anchor_canonical_day`.
 *
 * - Year iteration is one step at a time. For the toy fixture (40
 *   days/year) and the property-test bound (years in `[-1000, 1000]`)
 *   this is 2k iterations per call — well below noticeable cost. Real
 *   calendars with 365-day years use the same loop; if performance
 *   becomes a concern we can memoise per-year `daysFromEra(0..N)` runs
 *   without changing the public API.
 */

/**
 * Convert a display date to a canonical day count.
 *
 * Rejects:
 *   - `unknown_era`            — `displayDate.era` not declared on calendar.
 *   - `invalid_month`          — month index outside `[1, cycles.year.months.length]`.
 *   - `invalid_day_of_month`   — day index outside `[1, monthLength(year, month)]`.
 *   - `day_before_era_start`   — resulting canonical day precedes the era's `start_day`.
 *   - `malformed_calendar`     — internal invariant violation (should never fire on
 *                                a `CalendarSchema`-validated input).
 */
export function toCanonicalDay(
  calendar: Calendar,
  displayDate: DisplayDate,
): CalendarResult<CanonicalDay> {
  const era = findEraById(calendar, displayDate.era);
  if (era === null) {
    return err(
      'unknown_era',
      `era '${displayDate.era}' is not declared on calendar '${calendar.id}'`,
      {
        era: displayDate.era,
        calendar: calendar.id,
      },
    );
  }

  // 1. Validate month / day.
  const monthCount = monthsCycle(calendar).months.length;
  if (displayDate.month < 1 || displayDate.month > monthCount) {
    return err('invalid_month', `month ${displayDate.month} out of range [1, ${monthCount}]`, {
      month: displayDate.month,
      max: monthCount,
    });
  }
  const len = monthLength(calendar, displayDate.year, displayDate.month);
  if (displayDate.day < 1 || displayDate.day > len) {
    return err(
      'invalid_day_of_month',
      `day ${displayDate.day} out of range [1, ${len}] for ${displayDate.year}/${displayDate.month}`,
      { day: displayDate.day, max: len, year: displayDate.year, month: displayDate.month },
    );
  }

  // 2. Days from era's first day to the target.
  const offsetFromEraStart = daysFromEraStart(calendar, era, displayDate);

  // 3. Raw position (origin-display-date → 0 frame) of the origin
  //    display date, used to re-base era start_days onto the shared axis.
  const originRaw = originRawCanonical(calendar);
  if (!originRaw.ok) return originRaw;

  // canonical_day(target)
  //   = era.start_day + offsetFromEraStart           (raw axis, origin→0)
  //   - originRaw.value                              (re-base so origin = 0)
  //   + calendar.anchor_canonical_day               (shift onto shared axis)
  // (era.start_day is null only for a leading era; in that case the era's
  // raw start is resolved against the origin display date — see
  // `canonicalDayOfEraStart`.)
  // `era.day_offset` (#240) is the inverse of the splice applied in
  // `toDisplayDate`: the displayed date is shifted +day_offset days, so a
  // display date maps back to a canonical day that is day_offset SMALLER.
  const eraStartCanonical = canonicalDayOfEraStart(calendar, era);
  const canonical =
    eraStartCanonical +
    offsetFromEraStart -
    (era.day_offset ?? 0) -
    originRaw.value +
    calendar.anchor_canonical_day;

  // 4. Era boundary check.
  //
  // For FORWARD eras (the common case), `start_day` is the era's lower
  //   canonical bound: dates with `canonical < start_day` are rejected
  //   (e.g. a `ce y0` date doesn't exist).
  //
  // For BACKWARD eras (BCE-style, year_one closest to the epoch), the
  //   era's canonical interval is `(-inf, start_day + year_one_length)`.
  //   Dates that fall AFTER that ceiling are rejected, but the era
  //   extends arbitrarily far in the negative direction — there is no
  //   lower bound. The check below mirrors the forward case.
  //
  // `start_day: null` means "no boundary in either direction" — the
  // leading era spans the whole timeline.
  if (era.start_day !== null) {
    const shift = axisShift(calendar);
    if (!shift.ok) return shift;
    const eraStartOnAxis = era.start_day + shift.value;
    if (era.direction === 'forward' && canonical < eraStartOnAxis) {
      return err(
        'day_before_era_start',
        `date precedes era '${era.id}' start_day (${era.start_day})`,
        { era: era.id, canonical, era_start_day: era.start_day },
      );
    }
    if (era.direction === 'backward') {
      const ceiling = eraStartOnAxis + yearOneLengthForBackwardEra(calendar, era);
      if (canonical >= ceiling) {
        return err(
          'day_before_era_start',
          `date is past era '${era.id}' year_one window (canonical=${canonical}, ceiling=${ceiling})`,
          { era: era.id, canonical, era_ceiling: ceiling },
        );
      }
    }
  }

  return ok(brandDay(canonical));
}

/**
 * Convert a canonical day count to a display date.
 *
 * The returned `ExpandedDisplayDate` carries the projected weekday when
 * the calendar declares a `week` cycle; consumers that only need the
 * `(era, year, month, day)` quad can ignore the extra field.
 *
 * Failure modes:
 *   - `before_first_era` — the canonical day falls before every era's
 *     `start_day` and the leading era has a non-null `start_day`. (A
 *     calendar with a leading `start_day: null` era never produces this.)
 *   - `malformed_calendar` — internal invariant violation.
 */
export function toDisplayDate(
  calendar: Calendar,
  canonicalDay: CanonicalDay | number,
): CalendarResult<ExpandedDisplayDate> {
  // 1. Find the era covering `canonicalDay`: the last era whose `start_day`
  //    is non-null and `<= canonicalDay`. If none match, fall back to the
  //    leading era iff its `start_day` is null; otherwise `before_first_era`.
  const era = findEraForCanonicalDay(calendar, canonicalDay);
  if (era === null) {
    return err('before_first_era', `canonical day ${canonicalDay} precedes every era's start_day`, {
      canonical: canonicalDay,
    });
  }

  // 2. Days from era's first day to the target on the canonical axis.
  //    The era's `day_offset` (#240) splices the DISPLAYED day count
  //    forward at the boundary (Julian → Gregorian's 10-day skip): we
  //    decompose `canonicalDay + day_offset` within the era so the
  //    displayed year/month/day jumps ahead while the canonical axis (and
  //    the era covering it, picked above) stays untouched.
  const eraStartShared = sharedAxisEraStart(calendar, era);
  if (!eraStartShared.ok) return eraStartShared;
  const offsetFromEraStart = canonicalDay + (era.day_offset ?? 0) - eraStartShared.value;

  // 3. Walk the year axis from `era.year_one` outward until we find the
  //    year containing the target day-of-year.
  const located = locateYearAndDayOfYear(calendar, era, offsetFromEraStart);
  const { monthIndex, day } = monthAndDayFromDayOfYear(calendar, located.year, located.dayOfYear);

  const result: ExpandedDisplayDate = {
    era: era.id,
    year: located.year,
    month: monthIndex,
    day,
  };
  const weekday = projectWeekday(calendar, canonicalDay);
  if (weekday !== undefined) result.weekday = weekday;
  return ok(result);
}

/**
 * The calendar's anchor display date — where `anchor_canonical_day` lands
 * in this calendar's own coordinates (ADR-0006 §2). By construction this
 * is the origin display date (first era, `year_one`, month 1, day 1), now
 * pinned onto the shared axis. Surfaces that need a neutral "starting"
 * display date (e.g. a date picker with nothing selected) use this in
 * place of the former `calendar.epoch`.
 */
export function anchorDisplayDate(calendar: Calendar): CalendarResult<ExpandedDisplayDate> {
  return toDisplayDate(calendar, brandDay(calendar.anchor_canonical_day));
}

// ---------- internal helpers ----------

function findEraById(calendar: Calendar, eraId: string): Era | null {
  for (const era of calendar.eras) {
    if (era.id === eraId) return era;
  }
  return null;
}

/**
 * Pick the era covering `canonicalDay`.
 *
 * - FORWARD eras have a canonical interval `[start_day, +inf)`. The
 *   covering era is the one with the greatest `start_day <= canonicalDay`.
 * - BACKWARD eras have a canonical interval `(-inf, start_day + year_one_length)`.
 *   They cover dates STRICTLY BELOW `start_day + year_one_length` — i.e.
 *   their interval extends from the epoch direction back to negative
 *   infinity. They are eligible only when no forward era covers the day.
 * - A leading era with `start_day: null` is the fallback when no other
 *   era qualifies (e.g. a single-era calendar that spans the whole
 *   timeline).
 */
function findEraForCanonicalDay(calendar: Calendar, canonicalDay: number): Era | null {
  const shift = axisShift(calendar);
  // A malformed origin makes era placement undefined; fail closed to the
  // leading era (the public entry points re-surface the malformed error).
  const delta = shift.ok ? shift.value : 0;

  let bestForward: Era | null = null;
  let bestForwardStart = -Infinity;
  let bestBackward: Era | null = null;
  let bestBackwardCeiling = Infinity;
  let leading: Era | null = null;

  for (const era of calendar.eras) {
    if (era.start_day === null) {
      if (leading === null) leading = era;
      continue;
    }
    const eraStartOnAxis = era.start_day + delta;
    if (era.direction === 'forward') {
      if (eraStartOnAxis <= canonicalDay && eraStartOnAxis > bestForwardStart) {
        bestForward = era;
        bestForwardStart = eraStartOnAxis;
      }
    } else {
      const ceiling = eraStartOnAxis + yearOneLengthForBackwardEra(calendar, era);
      // Strictly less: a canonical day exactly equal to `ceiling` is the
      // first day of the next (forward) era, not a backward day.
      if (canonicalDay < ceiling && ceiling < bestBackwardCeiling) {
        bestBackward = era;
        bestBackwardCeiling = ceiling;
      }
    }
  }

  if (bestForward !== null) return bestForward;
  if (bestBackward !== null) return bestBackward;
  return leading;
}

/**
 * Days from `(era.year_one, 1, 1)` to `displayDate` on the era's
 * internal axis. For forward eras the result is non-negative iff
 * `displayDate.year >= era.year_one`; for backward eras the year axis
 * is mirrored.
 */
function daysFromEraStart(calendar: Calendar, era: Era, displayDate: DisplayDate): number {
  const targetLinearYear =
    era.direction === 'forward' ? displayDate.year - era.year_one : era.year_one - displayDate.year;

  // Days from start of era (linear year 0) to start of `targetLinearYear`.
  let days = 0;
  if (targetLinearYear >= 0) {
    for (let ly = 0; ly < targetLinearYear; ly++) {
      days += yearLength(calendar, linearToDisplayYear(era, ly));
    }
  } else {
    for (let ly = -1; ly >= targetLinearYear; ly--) {
      days -= yearLength(calendar, linearToDisplayYear(era, ly));
    }
  }

  // Add days from start of target year to target month/day.
  days += dayOfYear(calendar, displayDate.year, displayDate.month, displayDate.day);

  return days;
}

/** Map a linear-year index back to a display year, honouring era direction. */
function linearToDisplayYear(era: Era, linearYear: number): number {
  return era.direction === 'forward' ? era.year_one + linearYear : era.year_one - linearYear;
}

/**
 * Walk the year axis from `era.year_one` to find the year containing
 * `offsetFromEraStart` and the (0-indexed) day-of-year within it.
 */
function locateYearAndDayOfYear(
  calendar: Calendar,
  era: Era,
  offsetFromEraStart: number,
): { year: number; dayOfYear: number } {
  let remaining = offsetFromEraStart;
  let linearYear = 0;

  if (remaining >= 0) {
    while (true) {
      const len = yearLength(calendar, linearToDisplayYear(era, linearYear));
      if (remaining < len) {
        return { year: linearToDisplayYear(era, linearYear), dayOfYear: remaining };
      }
      remaining -= len;
      linearYear++;
    }
  }

  // Negative offset: walk backward. Subtract the length of the previous
  // linear year and check whether the (negated) remainder fits.
  while (remaining < 0) {
    linearYear--;
    const len = yearLength(calendar, linearToDisplayYear(era, linearYear));
    remaining += len;
  }
  return { year: linearToDisplayYear(era, linearYear), dayOfYear: remaining };
}

/**
 * The calendar's ORIGIN display date: the first era's
 * `(year_one, month 1, day 1)`. `anchor_canonical_day` declares where
 * this point sits on the shared day axis (ADR-0006 §2). Computed (not
 * stored) so the schema stays minimal and self-consistent.
 */
function originDisplayDate(calendar: Calendar): DisplayDate | null {
  const first = calendar.eras[0];
  if (first === undefined) return null;
  return { era: first.id, year: first.year_one, month: 1, day: 1 };
}

/**
 * Resolve the RAW canonical day on which an era begins — i.e. on the
 * "origin display date → 0" frame, before the `anchor_canonical_day`
 * shift onto the shared axis. For an era with `start_day: null` (the
 * leading era), the raw start is derived by inverting the origin display
 * date (which projects to raw 0 by construction).
 */
function canonicalDayOfEraStart(calendar: Calendar, era: Era): number {
  if (era.start_day !== null) return era.start_day;
  const origin = originDisplayDate(calendar);
  if (origin === null) return 0;
  // Leading era: its first day sits at raw `-daysFromEraStart(origin)`
  // so that the origin display date itself projects to raw 0.
  return -daysFromEraStart(calendar, era, origin);
}

/**
 * The RAW canonical position of the origin display date (origin→0 frame).
 * By the leading-era convention this is 0, but the general computation is
 * kept so a calendar whose origin lives in a non-leading era stays
 * self-consistent. Mirrors the old `canonicalDayOfEpochAnchor` but pivots
 * on the origin display date rather than a stored `epoch`.
 */
function originRawCanonical(calendar: Calendar): CalendarResult<number> {
  const origin = originDisplayDate(calendar);
  if (origin === null) {
    return err('malformed_calendar', `calendar '${calendar.id}' declares no eras`, {
      calendar: calendar.id,
    });
  }
  const originEra = findEraById(calendar, origin.era);
  if (originEra === null) {
    return err(
      'malformed_calendar',
      `calendar '${calendar.id}' origin references unknown era '${origin.era}'`,
      { era: origin.era, calendar: calendar.id },
    );
  }
  const eraStartCanonical = canonicalDayOfEraStart(calendar, originEra);
  const offset = daysFromEraStart(calendar, originEra, origin);
  return ok(eraStartCanonical + offset);
}

/**
 * The global shift from the raw (origin→0) frame onto the shared canonical
 * axis: `anchor_canonical_day - originRaw`. Adding this to any raw era
 * position yields its position on the shared axis.
 */
function axisShift(calendar: Calendar): CalendarResult<number> {
  const originRaw = originRawCanonical(calendar);
  if (!originRaw.ok) return originRaw;
  return ok(calendar.anchor_canonical_day - originRaw.value);
}

/**
 * The canonical day on which an era begins on the SHARED axis (raw era
 * start + {@link axisShift}). Used wherever a `CanonicalDay` is compared
 * against an era boundary.
 */
function sharedAxisEraStart(calendar: Calendar, era: Era): CalendarResult<number> {
  const shift = axisShift(calendar);
  if (!shift.ok) return shift;
  return ok(canonicalDayOfEraStart(calendar, era) + shift.value);
}

/**
 * Length of the year_one of a BACKWARD era — needed to compute the
 * canonical ceiling of the era's interval. Per the engine's display-year
 * convention, year_one is the display year closest to the epoch, so its
 * length is whatever `yearLength` reports for that display year under
 * the calendar's leap rule.
 */
function yearOneLengthForBackwardEra(calendar: Calendar, era: Era): number {
  return yearLength(calendar, era.year_one);
}

/**
 * Project the weekday for a canonical day, driven off the bag's atomic
 * `week` cycle (A2, #231). The legacy `{ year, week }` arm normalizes to a
 * `week` atomic cycle carrying the weekday names as a `list` label, so
 * `cycleLabel(calendar, 'week', day)` resolves the same name the old
 * `weekdaysCycle`-based projection did — byte-identical for the bundled
 * calendars, and now also defined for a bag-form calendar's labeled atomic
 * cycle. Falls back to `undefined` when the calendar declares no weekday
 * cycle (the `weekdaysCycle` accessor returns `undefined`).
 */
function projectWeekday(calendar: Calendar, canonicalDay: number): string | undefined {
  if (weekdaysCycle(calendar) === undefined) return undefined;
  const label = cycleLabel(calendar, weekdayCycleName(calendar), canonicalDay);
  return label ?? undefined;
}

/**
 * The name of the calendar's weekday cycle in the bag. The legacy arm
 * normalizes to a cycle named `'week'`; a bag-form calendar's labeled
 * atomic cycle may use any name, so resolve it from the bag (the first
 * atomic cycle carrying a `list` label — the same rule `weekdaysCycle`
 * uses).
 */
function weekdayCycleName(calendar: Calendar): string {
  const bag = calendarBag(calendar);
  for (const cycle of bag) {
    if (cycle.structure.kind === 'atomic' && cycle.labels?.kind === 'list') {
      return cycle.name;
    }
  }
  return 'week';
}
